import React, { useEffect, useMemo, useState } from "react"
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    IconButton,
    LinearProgress,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import MotorcycleIcon from "@material-ui/icons/Motorcycle"
import RefreshIcon from "@material-ui/icons/Refresh"
import LinkIcon from "@material-ui/icons/Link"
import WhatsAppIcon from "@material-ui/icons/WhatsApp"
import RoomIcon from "@material-ui/icons/Room"
import LocationOffIcon from "@material-ui/icons/LocationOff"
import CloseIcon from "@material-ui/icons/Close"
// integracao.js é módulo JS legado (sem tipagem própria).
import { buscarInformacoesPedido } from "../../../services/integracao"
import { useSnackbar } from "../../../utils/notificacoes"
import {
    jaRoteirizado,
    temCoordenadas,
    WHATSAPP_COMERCIAL,
    type FiltroStatusRota,
    type PedidoRoteirizavel,
} from "../../../services/roteirizacao"
import { useRoteirizacaoContext } from "../context/useRoteirizacaoContext"
import { RoteirizacaoActionsTypes } from "../context/roteirizacaoActions"
import { MapaRoteirizacao } from "../mapa/MapaRoteirizacao"
import type { MarcadorPedido } from "../mapa/tipos"
import { COR_ACEITO, COR_EM_ROTA, COR_PREPARO, COR_PRONTO, corDoStatus } from "../mapa/coresEntregador"
import { CASA_SVG_DATA_URL } from "../mapa/icones"

const useStyles = makeStyles(theme => ({
    container: {
        boxSizing: "border-box",
        height: "100vh",
        paddingTop: 70,
        paddingLeft: 80,
        paddingRight: 12,
        paddingBottom: 12,
        [theme.breakpoints.down("sm")]: {
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 64,
            height: "auto",
            minHeight: "100vh",
        },
    },
    conteudo: {
        display: "flex",
        gap: theme.spacing(2),
        height: "100%",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            height: "auto",
        },
    },
    painelLateral: {
        width: 380,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        borderRadius: 16,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            height: "auto",
        },
    },
    cabecalhoPainel: {
        padding: theme.spacing(2),
        borderBottom: "1px solid #e9ecef",
    },
    titulo: {
        fontWeight: 700,
        color: "#2c3e50",
    },
    subtitulo: {
        color: "#7f8c8d",
        fontSize: "0.8rem",
    },
    usoWrapper: {
        marginTop: theme.spacing(1.5),
    },
    controles: {
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.5),
        borderBottom: "1px solid #e9ecef",
    },
    linhaControles: {
        display: "flex",
        gap: theme.spacing(1),
        alignItems: "center",
    },
    corDot: {
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: "50%",
        marginRight: 8,
        flexShrink: 0,
    },
    listaPedidos: {
        flex: 1,
        overflowY: "auto",
        padding: theme.spacing(1),
        [theme.breakpoints.down("sm")]: {
            maxHeight: 340,
        },
    },
    cardPedido: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        borderRadius: 10,
        cursor: "pointer",
        marginBottom: theme.spacing(0.5),
        "&:hover": { backgroundColor: "#f5f7fa" },
    },
    cardPedidoSelecionado: {
        backgroundColor: "#e8f5e9",
        "&:hover": { backgroundColor: "#e8f5e9" },
    },
    nomeCliente: {
        fontWeight: 600,
        color: "#2c3e50",
        fontSize: "0.9rem",
    },
    infoPedido: {
        color: "#7f8c8d",
        fontSize: "0.78rem",
    },
    areaMapa: {
        position: "relative",
        flex: 1,
        minWidth: 0,
        height: "100%",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "#eceff1",
        [theme.breakpoints.down("sm")]: {
            height: "60vh",
            minHeight: 320,
        },
    },
    cartaoDetalhe: {
        position: "absolute",
        top: 12,
        right: 12,
        width: 300,
        maxWidth: "calc(100% - 24px)",
        maxHeight: "calc(100% - 24px)",
        overflowY: "auto",
        padding: theme.spacing(1.5),
        borderRadius: 12,
        zIndex: 5,
        backgroundColor: "#fff",
    },
    linhaDetalhe: {
        display: "flex",
        gap: 6,
        margin: "3px 0",
    },
    rotuloDetalhe: {
        color: "#7f8c8d",
        minWidth: 92,
        flexShrink: 0,
    },
    valorDetalhe: {
        fontWeight: 500,
        flex: 1,
        wordBreak: "break-word",
    },
    bannerLimite: {
        backgroundColor: "#fff3e0",
        border: "1px solid #ffb74d",
        borderRadius: 10,
        padding: theme.spacing(1.5),
        marginTop: theme.spacing(1),
    },
    botaoAssociar: {
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    botaoWhats: {
        backgroundColor: "#075E54",
        color: "white",
        "&:hover": { backgroundColor: "#054c44" },
    },
}))

function corDoPedido(pedido: PedidoRoteirizavel): string {
    // Mesmas cores das raias do Recebedor (amarelo aceito, laranja preparo, roxo pronto, azul em rota).
    return corDoStatus(pedido.codigoStatus)
}

function formatarMoeda(valor: number | null | undefined): string {
    if (valor == null) return "—"
    return `R$ ${valor.toFixed(2).replace(".", ",")}`
}

function formatarDataHora(ms: number): string {
    if (!ms) return "—"
    try {
        return new Date(ms).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        })
    } catch {
        return "—"
    }
}

// O detalhe (integracao/orders/{id}) traz o endereço em deliveryAddress; usa o
// formattedAddress quando existe, senão compõe rua/nº - bairro - cidade/UF - compl.
/* eslint-disable @typescript-eslint/no-explicit-any */
function formatarEnderecoEntrega(addr: any): string | null {
    if (!addr) return null
    if (addr.formattedAddress) return addr.formattedAddress
    const rua = [addr.streetName, addr.streetNumber].filter(Boolean).join(", ")
    const cidadeUf = addr.city && addr.state ? `${addr.city}/${addr.state}` : addr.city || ""
    return [rua, addr.neighborhood, cidadeUf, addr.complement].filter(Boolean).join(" - ") || null
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const FILTROS_STATUS: { chave: keyof FiltroStatusRota; label: string }[] = [
    { chave: "aceitos", label: "Aceitos" },
    { chave: "preparo", label: "Em preparo" },
    { chave: "entrega", label: "Em entrega" },
    { chave: "entregues", label: "Entregues" },
    { chave: "cancelados", label: "Cancelados" },
]

function abrirWhatsappComercial() {
    const texto = encodeURIComponent(
        "Oi, tudo bem? Já utilizei minha franquia de roteirização no painel e gostaria de ativar a funcionalidade e ajustar minha mensalidade."
    )
    window.open(`https://api.whatsapp.com/send?phone=${WHATSAPP_COMERCIAL}&text=${texto}`, "_blank")
}

export function PainelRoteirizacao() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const {
        dados,
        dispatch,
        erroCarregamento,
        provedorMapa,
        origem,
        tokenMapbox,
        recarregar,
        associarSelecionados,
    } = useRoteirizacaoContext()

    const [upsellAberto, setUpsellAberto] = useState(false)

    // Card de detalhes ao clicar num ponto do mapa (estilo Saipos). O detalhe completo
    // (endereço, previsão) é buscado sob demanda em integracao/orders/{id} só quando o card abre.
    const autorizacaoDetalhe = localStorage.getItem("chaveSistema") || ""
    const [detalheId, setDetalheId] = useState<number | null>(null)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [detalheCarregado, setDetalheCarregado] = useState<any>(null)
    const [carregandoDetalhe, setCarregandoDetalhe] = useState(false)

    useEffect(() => {
        if (detalheId == null) {
            setDetalheCarregado(null)
            return
        }
        let ativo = true
        setDetalheCarregado(null)
        setCarregandoDetalhe(true)
        buscarInformacoesPedido(autorizacaoDetalhe, detalheId)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((info: any) => {
                if (ativo && info && !info.retornoErro && info.id != null) {
                    setDetalheCarregado(info)
                }
            })
            .catch(() => {})
            .finally(() => {
                if (ativo) setCarregandoDetalhe(false)
            })
        return () => {
            ativo = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detalheId])

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento, enqueueSnackbar])

    // Com o recurso ROTEIRIZACAO ativo na assinatura, as roteirizações são ilimitadas:
    // sem teto, sem banner de limite e sem upsell.
    const restantes = dados.ilimitado
        ? Number.POSITIVE_INFINITY
        : Math.max(0, dados.limite - dados.usados)
    const limiteAtingido = !dados.ilimitado && dados.usados >= dados.limite
    const progresso =
        dados.limite > 0 ? Math.min(100, (dados.usados / dados.limite) * 100) : 0

    const pedidosComCoordenadas = useMemo(
        () => dados.pedidos.filter(temCoordenadas),
        [dados.pedidos]
    )
    const pedidosSemCoordenadas = useMemo(
        () => dados.pedidos.filter(p => !temCoordenadas(p)),
        [dados.pedidos]
    )

    const marcadores: MarcadorPedido[] = useMemo(
        () =>
            pedidosComCoordenadas.map(p => ({
                id: p.id,
                latitude: p.latitude as number,
                longitude: p.longitude as number,
                cliente: p.cliente,
                shortReference: p.shortReference,
                nomeEntregador: p.motoboyPedido,
                selecionado: dados.pedidosSelecionados.includes(p.id),
                cor: corDoPedido(p),
            })),
        [pedidosComCoordenadas, dados.pedidosSelecionados]
    )

    const togglePedido = (id: number) =>
        dispatch({ type: RoteirizacaoActionsTypes.TOGGLE_PEDIDO, payload: id })

    const selecionarTodosComLocalizacao = () =>
        dispatch({
            type: RoteirizacaoActionsTypes.SELECIONAR_IDS,
            payload: pedidosComCoordenadas.map(p => p.id),
        })

    const limparSelecao = () => dispatch({ type: RoteirizacaoActionsTypes.LIMPAR_SELECAO })

    const toggleFiltroStatus = (chave: keyof FiltroStatusRota) =>
        dispatch({
            type: RoteirizacaoActionsTypes.SET_FILTRO_STATUS,
            payload: { ...dados.filtroStatus, [chave]: !dados.filtroStatus[chave] },
        })

    const handleAssociar = async () => {
        const resultado = await associarSelecionados()
        if (resultado.erro) {
            enqueueSnackbar(resultado.erro, { variant: "warning" })
            return
        }
        if (resultado.associados > 0) {
            enqueueSnackbar(
                `${resultado.associados} pedido(s) associado(s) ao entregador.`,
                { variant: "success" }
            )
        }
        if (resultado.limiteAtingido || (restantes <= 0 && resultado.associados === 0)) {
            setUpsellAberto(true)
        } else if (resultado.associados === 0) {
            enqueueSnackbar("Nenhum pedido foi associado.", { variant: "info" })
        }
    }

    const handleClickAssociar = () => {
        if (limiteAtingido) {
            // Só permite prosseguir se houver reatribuição (pedidos já roteirizados).
            const selecionados = dados.pedidos.filter(p =>
                dados.pedidosSelecionados.includes(p.id)
            )
            const temNovos = selecionados.some(p => !jaRoteirizado(p))
            const soReatribuicao = selecionados.length > 0 && !temNovos
            if (!soReatribuicao) {
                setUpsellAberto(true)
                return
            }
        }
        handleAssociar()
    }

    const qtdSelecionados = dados.pedidosSelecionados.length
    const entregadorSelecionado = dados.entregadores.find(
        e => e.id === dados.entregadorSelecionadoId
    )
    const podeAssociar = qtdSelecionados > 0 && !!dados.entregadorSelecionadoId

    return (
        <Box className={classes.container}>
            <Box className={classes.conteudo}>
                {/* Painel lateral: uso, controles e lista de pedidos */}
                <Paper className={classes.painelLateral} elevation={0}>
                    <Box className={classes.cabecalhoPainel}>
                        <Typography variant="h6" className={classes.titulo}>
                            Roteirização
                        </Typography>
                        <Typography className={classes.subtitulo}>
                            Associe um entregador a um ou mais pedidos de delivery pelo mapa.
                        </Typography>

                        <Box
                            display="flex"
                            flexWrap="wrap"
                            style={{ gap: 12, marginTop: 6, fontSize: "0.75rem", color: "#555" }}
                        >
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <img src={CASA_SVG_DATA_URL} alt="" width={16} height={16} />
                                Restaurante
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span
                                    className={classes.corDot}
                                    style={{ backgroundColor: COR_ACEITO, margin: 0 }}
                                />
                                Aceito
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span
                                    className={classes.corDot}
                                    style={{ backgroundColor: COR_PREPARO, margin: 0 }}
                                />
                                Em preparação
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span
                                    className={classes.corDot}
                                    style={{ backgroundColor: COR_PRONTO, margin: 0 }}
                                />
                                Aguardando entregador/retirada
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span
                                    className={classes.corDot}
                                    style={{ backgroundColor: COR_EM_ROTA, margin: 0 }}
                                />
                                Em entrega
                            </span>
                        </Box>

                        <Box className={classes.usoWrapper}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    Pedidos roteirizados no mês
                                </Typography>
                                <Typography
                                    variant="body2"
                                    style={{
                                        fontWeight: 700,
                                        color: dados.ilimitado
                                            ? "#2e7d32"
                                            : limiteAtingido
                                            ? "var(--cor-erro)"
                                            : "#2c3e50",
                                    }}
                                >
                                    {dados.ilimitado
                                        ? `${dados.usados} · Ilimitado`
                                        : `${dados.usados} / ${dados.limite}`}
                                </Typography>
                            </Box>
                            {!dados.ilimitado && (
                                <LinearProgress
                                    variant="determinate"
                                    value={progresso}
                                    style={{ marginTop: 6, height: 8, borderRadius: 4 }}
                                    color={limiteAtingido ? "secondary" : "primary"}
                                />
                            )}
                            {limiteAtingido && (
                                <Box className={classes.bannerLimite}>
                                    <Typography variant="body2" style={{ color: "#e65100" }}>
                                        Você atingiu o limite de {dados.limite} pedidos roteirizados
                                        este mês. Para continuar, acione nosso comercial e ative a
                                        funcionalidade.
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        className={classes.botaoWhats}
                                        startIcon={<WhatsAppIcon />}
                                        style={{ marginTop: 8 }}
                                        onClick={() => setUpsellAberto(true)}
                                    >
                                        Falar com o comercial
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Controles */}
                    <Box className={classes.controles}>
                        {/* Alinha pelo topo: o helperText aumenta a altura do campo e, centralizado,
                            o botão ficava abaixo do input. */}
                        <Box className={classes.linhaControles} style={{ alignItems: "flex-start" }}>
                            <TextField
                                label="Dia"
                                variant="outlined"
                                size="small"
                                placeholder="dd/mm/aaaa"
                                value={dados.data}
                                onChange={e =>
                                    dispatch({
                                        type: RoteirizacaoActionsTypes.SET_DATA,
                                        payload: e.target.value,
                                    })
                                }
                                onKeyDown={e => {
                                    if (e.key === "Enter") recarregar()
                                }}
                                helperText="Enter ou Atualizar para buscar"
                                style={{ flex: 1 }}
                            />
                            <Tooltip title="Atualizar pedidos">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => recarregar()}
                                    style={{ minWidth: 40, height: 40 }}
                                >
                                    <RefreshIcon />
                                </Button>
                            </Tooltip>
                        </Box>

                        <Box>
                            <Typography
                                variant="caption"
                                style={{ color: "#7f8c8d", fontWeight: 600 }}
                            >
                                Status no mapa
                            </Typography>
                            <FormGroup row>
                                {FILTROS_STATUS.map(({ chave, label }) => (
                                    <FormControlLabel
                                        key={chave}
                                        style={{ marginLeft: 0, marginRight: 6 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                color="primary"
                                                style={{ padding: 3 }}
                                                checked={dados.filtroStatus[chave]}
                                                onChange={() => toggleFiltroStatus(chave)}
                                            />
                                        }
                                        label={
                                            <span style={{ fontSize: "0.8rem" }}>{label}</span>
                                        }
                                    />
                                ))}
                            </FormGroup>
                        </Box>

                        <TextField
                            select
                            label="Entregador"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={dados.entregadorSelecionadoId ?? ""}
                            onChange={e =>
                                dispatch({
                                    type: RoteirizacaoActionsTypes.SET_ENTREGADOR,
                                    payload: e.target.value ? Number(e.target.value) : null,
                                })
                            }
                        >
                            <MenuItem value="">
                                <em>Selecione um entregador</em>
                            </MenuItem>
                            {dados.entregadores.map(entregador => (
                                <MenuItem key={entregador.id} value={entregador.id}>
                                    {entregador.nome}
                                    {entregador.placaVeiculo ? ` • ${entregador.placaVeiculo}` : ""}
                                </MenuItem>
                            ))}
                        </TextField>
                        {dados.entregadores.length === 0 && (
                            <Typography variant="caption" style={{ color: "var(--cor-erro)" }}>
                                Nenhum entregador ativo cadastrado. Cadastre em Entregadores.
                            </Typography>
                        )}

                        <Button
                            variant="contained"
                            className={classes.botaoAssociar}
                            startIcon={<MotorcycleIcon />}
                            disabled={!podeAssociar}
                            onClick={handleClickAssociar}
                            fullWidth
                        >
                            {entregadorSelecionado
                                ? `Associar ${qtdSelecionados} pedido(s) a ${entregadorSelecionado.nome}`
                                : `Associar ${qtdSelecionados} pedido(s)`}
                        </Button>

                        <Box className={classes.linhaControles}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={selecionarTodosComLocalizacao}
                                disabled={pedidosComCoordenadas.length === 0}
                            >
                                Selecionar todos
                            </Button>
                            <Button
                                size="small"
                                variant="text"
                                onClick={limparSelecao}
                                disabled={qtdSelecionados === 0}
                            >
                                Limpar seleção
                            </Button>
                        </Box>
                    </Box>

                    {/* Lista de pedidos */}
                    <Box className={classes.listaPedidos}>
                        {dados.pedidos.length === 0 ? (
                            <Box textAlign="center" p={3} style={{ color: "#7f8c8d" }}>
                                <RoomIcon style={{ fontSize: 40, marginBottom: 8 }} />
                                <Typography variant="body2">
                                    Nenhum pedido de delivery para este dia.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {pedidosComCoordenadas.map(pedido => {
                                    const selecionado = dados.pedidosSelecionados.includes(pedido.id)
                                    return (
                                        <Box
                                            key={pedido.id}
                                            className={`${classes.cardPedido} ${
                                                selecionado ? classes.cardPedidoSelecionado : ""
                                            }`}
                                            onClick={() => togglePedido(pedido.id)}
                                        >
                                            <Checkbox
                                                checked={selecionado}
                                                size="small"
                                                style={{ padding: 2 }}
                                                onClick={e => e.stopPropagation()}
                                                onChange={() => togglePedido(pedido.id)}
                                            />
                                            <span
                                                className={classes.corDot}
                                                style={{
                                                    backgroundColor: corDoPedido(pedido),
                                                    marginTop: 6,
                                                }}
                                            />
                                            <Box flex={1} minWidth={0}>
                                                <Typography className={classes.nomeCliente} noWrap>
                                                    #{pedido.shortReference} {pedido.cliente}
                                                </Typography>
                                                <Typography className={classes.infoPedido} noWrap>
                                                    {pedido.status}
                                                    {pedido.motoboyPedido
                                                        ? ` • ${pedido.motoboyPedido}`
                                                        : ""}
                                                </Typography>
                                            </Box>
                                            {jaRoteirizado(pedido) && (
                                                <Chip
                                                    size="small"
                                                    label="Roteirizado"
                                                    style={{
                                                        backgroundColor: "#e3f2fd",
                                                        color: "#1565c0",
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    )
                                })}

                                {pedidosSemCoordenadas.length > 0 && (
                                    <>
                                        <Divider style={{ margin: "8px 0" }} />
                                        <Typography
                                            variant="caption"
                                            style={{
                                                paddingLeft: 8,
                                                color: "#7f8c8d",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4,
                                            }}
                                        >
                                            <LocationOffIcon style={{ fontSize: 16 }} />
                                            Sem localização no mapa (ainda podem ser associados)
                                        </Typography>
                                        {pedidosSemCoordenadas.map(pedido => {
                                            const selecionado = dados.pedidosSelecionados.includes(
                                                pedido.id
                                            )
                                            return (
                                                <Box
                                                    key={pedido.id}
                                                    className={`${classes.cardPedido} ${
                                                        selecionado
                                                            ? classes.cardPedidoSelecionado
                                                            : ""
                                                    }`}
                                                    onClick={() => togglePedido(pedido.id)}
                                                >
                                                    <Checkbox
                                                        checked={selecionado}
                                                        size="small"
                                                        style={{ padding: 2 }}
                                                        onClick={e => e.stopPropagation()}
                                                        onChange={() => togglePedido(pedido.id)}
                                                    />
                                                    <Box flex={1} minWidth={0}>
                                                        <Typography
                                                            className={classes.nomeCliente}
                                                            noWrap
                                                        >
                                                            #{pedido.shortReference} {pedido.cliente}
                                                        </Typography>
                                                        <Typography
                                                            className={classes.infoPedido}
                                                            noWrap
                                                        >
                                                            {pedido.status}
                                                            {pedido.motoboyPedido
                                                                ? ` • ${pedido.motoboyPedido}`
                                                                : ""}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )
                                        })}
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                </Paper>

                {/* Mapa */}
                <Box className={classes.areaMapa}>
                    <MapaRoteirizacao
                        provedor={provedorMapa}
                        tokenMapbox={tokenMapbox}
                        origem={origem}
                        marcadores={marcadores}
                        onSelecionarPedido={id => setDetalheId(id)}
                    />
                    {detalheId != null &&
                        (() => {
                            const pedido = dados.pedidos.find(p => p.id === detalheId)
                            if (!pedido) return null
                            const selecionado = dados.pedidosSelecionados.includes(pedido.id)
                            const endereco = formatarEnderecoEntrega(detalheCarregado?.deliveryAddress)
                            const previsao = detalheCarregado?.deliveryDateTime as string | undefined
                            const linha = (rotulo: string, valor: string) => (
                                <Box className={classes.linhaDetalhe}>
                                    <Typography variant="body2" className={classes.rotuloDetalhe}>
                                        {rotulo}
                                    </Typography>
                                    <Typography variant="body2" className={classes.valorDetalhe}>
                                        {valor}
                                    </Typography>
                                </Box>
                            )
                            return (
                                <Paper className={classes.cartaoDetalhe} elevation={6}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            style={{ fontWeight: 600 }}
                                        >
                                            Pedido #{pedido.shortReference}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            aria-label="Fechar"
                                            onClick={() => setDetalheId(null)}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Divider style={{ margin: "4px 0 8px" }} />
                                    {linha("Cliente", pedido.cliente)}
                                    {pedido.telefone ? linha("Telefone", pedido.telefone) : null}
                                    {linha("Situação", pedido.status || "—")}
                                    {linha(
                                        "Entregador",
                                        pedido.motoboyPedido || "Não roteirizado"
                                    )}
                                    {linha(
                                        "Endereço",
                                        endereco || (carregandoDetalhe ? "carregando…" : "—")
                                    )}
                                    {linha("Taxa de entrega", formatarMoeda(pedido.taxaEntrega))}
                                    {linha("Valor total", formatarMoeda(pedido.valorTotal))}
                                    {linha("Data/hora", formatarDataHora(pedido.dataRealizacao))}
                                    {previsao ? linha("Previsão de entrega", previsao) : null}
                                    <Button
                                        size="small"
                                        variant={selecionado ? "outlined" : "contained"}
                                        color="primary"
                                        fullWidth
                                        style={{ marginTop: 10 }}
                                        onClick={() => togglePedido(pedido.id)}
                                    >
                                        {selecionado
                                            ? "Remover da seleção"
                                            : "Selecionar para alocar"}
                                    </Button>
                                    {pedido.linkPedido ? (
                                        <Button
                                            size="small"
                                            fullWidth
                                            startIcon={<LinkIcon />}
                                            href={pedido.linkPedido}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ marginTop: 6 }}
                                        >
                                            Ver pedido
                                        </Button>
                                    ) : null}
                                </Paper>
                            )
                        })()}
                </Box>
            </Box>

            {/* Dialog de upsell comercial */}
            <Dialog open={upsellAberto} onClose={() => setUpsellAberto(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Ative a Roteirização</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Você já utilizou {dados.usados} de {dados.limite} pedidos roteirizados
                        disponíveis neste mês. Para continuar roteirizando, acione nosso comercial
                        para ativar a funcionalidade e reajustar sua mensalidade.
                    </DialogContentText>
                    <Button
                        variant="contained"
                        fullWidth
                        className={classes.botaoWhats}
                        startIcon={<WhatsAppIcon />}
                        onClick={() => {
                            abrirWhatsappComercial()
                            setUpsellAberto(false)
                        }}
                    >
                        Falar com o comercial
                    </Button>
                    <Box textAlign="center" mt={1}>
                        <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                            <LinkIcon style={{ fontSize: 14, verticalAlign: "middle" }} /> WhatsApp
                            comercial: {WHATSAPP_COMERCIAL}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpsellAberto(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
